import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Legacy URL Redirect Handler (Client-Side Only)
 * Handles redirects from old sicunet.com URLs to new sicunet.com routes
 * This component should be placed in App.jsx to work on every route
 */
const LegacyRedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;

    // Legacy URL mapping from old sicunet.com structure to new sicunet.com routes
    const legacyRouteMap = {
      // Features routes (www.sicunet.com/features/...)
      // All features routes redirect to /product (product listing page)
      '/features': '/product',
      '/features/': '/product',
      '/features/nvrintegration': '/product',
      '/features/NVRIntegration': '/product',
      '/features/mobilelink': '/product',
      '/features/Mobilelink': '/product',
      '/features/schlagelockset': '/product',
      '/features/SchlageLockset': '/product',
      '/features/smartreports': '/product',
      '/features/SmartReports': '/product',
      '/features/videotagging': '/product',
      '/features/VideoTagging': '/product',

      // Subpage routes (www.sicunet.com/subpage/...)
      '/subpage': '/company',
      '/subpage/': '/company',
      '/subpage/company': '/company',
      '/subpage/Company': '/company',
      '/subpage/warrantypolicy': '/warrantypolicy',
      '/subpage/WarrantyPolicy': '/warrantypolicy',
      '/subpage/privacypolicy': '/privacypolicy',
      '/subpage/PrivacyPolicy': '/privacypolicy',
      '/subpage/release_note': '/releasenote',
      '/subpage/Release_Note': '/releasenote',

      // Product-related subpages (redirect to /product)
      '/subpage/1door.html': '/product',
      '/subpage/1Door.html': '/product',
      '/subpage/2door.html': '/product',
      '/subpage/2Door.html': '/product',
      '/subpage/4door.html': '/product',
      '/subpage/4Door.html': '/product',

      // Download routes (www.sicunet.com/download/...)
      // All download routes redirect to /user-interface
      '/download': '/user-interface',
      '/download/': '/user-interface',
      '/download/manual': '/user-interface',
      '/download/Manual': '/user-interface',
      '/download/cutsheet': '/user-interface',
      '/download/Cutsheet': '/user-interface',
      '/download/brochure': '/user-interface',
      '/download/Brochure': '/user-interface',

      // Manual/Wire Diagram routes
      // All manual routes redirect to /user-interface
      '/manual': '/user-interface',
      '/manual/': '/user-interface',
      '/manual/wire_diagram': '/user-interface',

      // Feature Matrix routes
      // Product-related, redirect to /product
      '/featurematrixbymodel': '/product',
      '/FeatureMatrixByModel': '/product',

      // Login routes
      '/login': '/login',
    };

    // Normalize path for case-insensitive matching
    const normalizedPath = currentPath.toLowerCase();

    // Check if current path matches any legacy route
    let redirectPath = null;

    // First check exact match (case-sensitive)
    if (legacyRouteMap[currentPath]) {
      redirectPath = legacyRouteMap[currentPath];
    }
    // Then check case-insensitive match
    else if (legacyRouteMap[normalizedPath]) {
      redirectPath = legacyRouteMap[normalizedPath];
    }
    // Handle partial matches
    else {
      // Features routes - ALL redirect to /product (product listing page)
      if (normalizedPath.startsWith('/features/')) {
        redirectPath = '/product';
      }
      // Subpage routes - check if path starts with /subpage/
      else if (normalizedPath.startsWith('/subpage/')) {
        // Extract the subpage name
        const subpageName = currentPath.substring('/subpage/'.length).toLowerCase();
        const subpageKey = `/subpage/${currentPath.substring('/subpage/'.length)}`;
        const normalizedSubpageKey = `/subpage/${subpageName}`;

        // Check if it's a door-related HTML file (1Door.html, 2Door.html, 4Door.html, etc.)
        if (normalizedSubpageKey.match(/\/subpage\/\d+door\.html$/i)) {
          redirectPath = '/product';
        }
        // Check exact match
        else if (legacyRouteMap[subpageKey]) {
          redirectPath = legacyRouteMap[subpageKey];
        } else if (legacyRouteMap[normalizedSubpageKey]) {
          redirectPath = legacyRouteMap[normalizedSubpageKey];
        } else {
          // Unknown subpage -> redirect to company page
          redirectPath = '/company';
        }
      }
      // Download routes - ALL redirect to /user-interface
      else if (normalizedPath.startsWith('/download/')) {
        redirectPath = '/user-interface';
      }
      // Manual routes - ALL redirect to /user-interface
      else if (normalizedPath.startsWith('/manual/')) {
        redirectPath = '/user-interface';
      }
    }

    // Perform redirect if needed
    if (redirectPath && redirectPath !== currentPath) {
      console.log(`Redirecting from legacy URL: ${currentPath} -> ${redirectPath}`);
      navigate(redirectPath, { replace: true });
    }
  }, [location, navigate]);

  return null; // This component doesn't render anything
};

export default LegacyRedirectHandler;